import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const SurveyContext = createContext(null);
const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const FREQ_KEY = 'doorly_last_survey';
const THIRTY_MIN = 30 * 60 * 1000;

// Post-closing always bypasses frequency limit
const ALWAYS_SHOW = ['post_closing', 'passive_feedback'];

export function SurveyProvider({ children }) {
  const { token, user } = useAuth();
  const [activeSurvey, setActiveSurvey] = useState(null); // { type, variant }
  const inProgress = useRef(false);

  const triggerSurvey = useCallback(async (type, opts = {}) => {
    if (!token || !user) return;
    if (inProgress.current) return;

    // Frequency limit (skip for always-show types)
    if (!ALWAYS_SHOW.includes(type)) {
      const last = localStorage.getItem(FREQ_KEY);
      if (last && Date.now() - Number(last) < THIRTY_MIN) return;
    }

    try {
      // Skip backend check for user-initiated feedback — always show
      if (!ALWAYS_SHOW.includes(type)) {
        const { data } = await axios.get(`${API}/api/surveys/check/${type}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!data.should_show) return;
      }

      // Mark as shown
      await axios.post(`${API}/api/surveys/shown`, { trigger_type: type }, {
        headers: { Authorization: `Bearer ${token}` },
      }).catch(() => {});

      localStorage.setItem(FREQ_KEY, String(Date.now()));
      inProgress.current = true;
      setActiveSurvey({ type, opts });
    } catch (_) {}
  }, [token, user]);

  const closeSurvey = useCallback(async (dismissed = true) => {
    if (activeSurvey && dismissed) {
      await axios.post(`${API}/api/surveys/dismiss`, { trigger_type: activeSurvey.type }, {
        headers: { Authorization: `Bearer ${token}` },
      }).catch(() => {});
    }
    inProgress.current = false;
    setActiveSurvey(null);
  }, [activeSurvey, token]);

  const submitSurvey = useCallback(async (payload) => {
    if (!activeSurvey) return;
    await axios.post(`${API}/api/surveys/submit`, {
      trigger_type: activeSurvey.type,
      ...payload,
    }, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
    inProgress.current = false;
    setActiveSurvey(null);
  }, [activeSurvey, token]);

  return (
    <SurveyContext.Provider value={{ triggerSurvey, closeSurvey, submitSurvey, activeSurvey }}>
      {children}
    </SurveyContext.Provider>
  );
}

export function useSurvey() {
  const ctx = useContext(SurveyContext);
  if (!ctx) throw new Error('useSurvey must be used within SurveyProvider');
  return ctx;
}
